import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { X, Plus, Upload, Shield, MapPin, Building2, RotateCcw } from "lucide-react";
import {
  getBlacklistedSubdivisions,
  addSubdivisionToBlacklist,
  removeSubdivisionFromBlacklist,
  saveBlacklistedSubdivisions,
  getBlacklistedTownships,
  addTownshipToBlacklist,
  removeTownshipFromBlacklist,
  saveBlacklistedTownships,
} from "@/utils/subdivisionBlacklist";

const DEFAULT_BLACKLISTED_TOWNSHIPS = [
  'LANCASTER', 'GLENN HEIGHTS', 'DUNCANVILLE', 'BALCH SPRINGS',
  'HUTCHINS', 'WILMER', 'SEAGOVILLE', 'COCKRELL HILL',
  'FERRIS', 'COMBINE', 'OVILLA',
];

function BlacklistTab({
  items,
  onAdd,
  onRemove,
  onBulkAdd,
  placeholder,
  label,
}: {
  items: string[];
  onAdd: (name: string) => void;
  onRemove: (name: string) => void;
  onBulkAdd: (names: string[]) => void;
  placeholder: string;
  label: string;
}) {
  const [newEntry, setNewEntry] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [showBulk, setShowBulk] = useState(false);

  const handleAdd = () => {
    if (!newEntry.trim()) return;
    onAdd(newEntry);
    setNewEntry("");
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          value={newEntry}
          onChange={(e) => setNewEntry(e.target.value)}
          placeholder={placeholder}
          className="text-sm"
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <Button size="sm" onClick={handleAdd} disabled={!newEntry.trim()}>
          <Plus className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowBulk(!showBulk)}>
          <Upload className="h-4 w-4" />
        </Button>
      </div>

      {showBulk && (
        <div className="space-y-2">
          <Textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={`Paste ${label} names (one per line, or comma-separated)...`}
            className="min-h-[80px] text-sm font-mono"
          />
          <Button
            size="sm"
            onClick={() => {
              const names = bulkText.split(/[\n,;]+/).map(s => s.toUpperCase().trim()).filter(Boolean);
              if (names.length > 0) {
                onBulkAdd(names);
                setBulkText("");
                setShowBulk(false);
              }
            }}
            className="w-full"
          >
            Add All
          </Button>
        </div>
      )}

      {items.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 max-h-[150px] overflow-y-auto">
          {items.sort().map((name) => (
            <Badge
              key={name}
              variant="secondary"
              className="text-xs gap-1 cursor-pointer hover:bg-destructive/20"
              onClick={() => onRemove(name)}
            >
              {name}
              <X className="h-3 w-3" />
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">No {label} blacklisted yet</p>
      )}
    </div>
  );
}

export function SubdivisionBlacklistManager() {
  const [subdivisions, setSubdivisions] = useState<string[]>([]);
  const [townships, setTownships] = useState<string[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    // Seed defaults on first use
    const existing = getBlacklistedTownships();
    if (existing.length === 0) {
      saveBlacklistedTownships(DEFAULT_BLACKLISTED_TOWNSHIPS);
      setTownships(DEFAULT_BLACKLISTED_TOWNSHIPS);
    } else {
      setTownships(existing);
    }
    setSubdivisions(getBlacklistedSubdivisions());
  }, []);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4" />
          Area Blacklist — Townships: {townships.length} | Subdivisions: {subdivisions.length}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-3">
          Records from blacklisted areas are saved with "blacklisted" status during import. Click a badge to remove it.
        </p>
        <Tabs defaultValue="townships">
          <TabsList className="mb-3">
            <TabsTrigger value="townships" className="gap-1.5 text-xs">
              <MapPin className="h-3.5 w-3.5" />
              Townships/Cities
            </TabsTrigger>
            <TabsTrigger value="subdivisions" className="gap-1.5 text-xs">
              <Building2 className="h-3.5 w-3.5" />
              Subdivisions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="townships">
            <BlacklistTab
              items={townships}
              placeholder="Township/city name (e.g., LANCASTER)..."
              label="townships"
              onAdd={(name) => {
                addTownshipToBlacklist(name);
                setTownships(getBlacklistedTownships());
              }}
              onRemove={(name) => {
                removeTownshipFromBlacklist(name);
                setTownships(getBlacklistedTownships());
              }}
              onBulkAdd={(names) => {
                const current = getBlacklistedTownships();
                const merged = [...new Set([...current, ...names])];
                saveBlacklistedTownships(merged);
                setTownships(merged);
                toast({ title: "Townships updated", description: `Total: ${merged.length}` });
              }}
            />
          </TabsContent>

          <TabsContent value="subdivisions">
            <BlacklistTab
              items={subdivisions}
              placeholder="Subdivision name..."
              label="subdivisions"
              onAdd={(name) => {
                addSubdivisionToBlacklist(name);
                setSubdivisions(getBlacklistedSubdivisions());
              }}
              onRemove={(name) => {
                removeSubdivisionFromBlacklist(name);
                setSubdivisions(getBlacklistedSubdivisions());
              }}
              onBulkAdd={(names) => {
                const current = getBlacklistedSubdivisions();
                const merged = [...new Set([...current, ...names])];
                saveBlacklistedSubdivisions(merged);
                setSubdivisions(merged);
                toast({ title: "Subdivisions updated", description: `Total: ${merged.length}` });
              }}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
